import React, { useEffect, useRef, useState } from "react";

import classNames from "classnames";
import { observer } from "mobx-react";
import { lighten } from "polished";
import PropTypes from "prop-types";
import styled from "styled-components";

import { useDataLayer } from "../../contexts/DataLayerContext";
import { useStores } from "../../contexts/StoreContext";
import Icon from "../../shared/Icon/Icon";
import Button from "../../shared/components/Button/Button";
import KeywordField from "../../shared/components/KeywordFilter/KeywordFilter";
import LocationSelect from "../../shared/components/LocationSelect/LocationSelect";
import RoleSelect from "../../shared/components/RoleSelect/RoleSelect";
import breakpoints from "../../shared/constants";
import { useAnalytics } from "../../shared/helpers/AnalyticsTrackingContext";
import Markdown from "../../shared/styles/Markdown";

const StyledSearchButton = styled(Button)`
  background-color: ${({ $buttonColor, theme }) =>
    `${$buttonColor || theme.secondaryColor}`};
  color: ${({ $buttonTextColour }) => $buttonTextColour || "white"};
  width: ${({ $buttonText }) => ($buttonText ? "" : "3rem")};
  height: 3rem;
  border: none;
  transition: background-color 0.2s linear;
  z-index: 2;

  @media (min-width: ${breakpoints.DESKTOP}) {
    padding: ${({ $buttonText }) => ($buttonText ? "0 1.5rem" : "0")};
  }

  &:hover {
    background-color: ${({ $buttonHoverColour, theme }) =>
      $buttonHoverColour || lighten(0.1, theme.secondaryColor)};
    box-shadow: ${({ $buttonHoverColour }) =>
      $buttonHoverColour
        ? `0 0 0 0.25rem ${lighten(0.05, $buttonHoverColour)}`
        : "none"};
  }

  &:active {
    background-color: ${({ $buttonHoverColour, theme }) =>
      $buttonHoverColour
        ? lighten(0.2, $buttonHoverColour)
        : lighten(0.2, theme.secondaryColor)};
    box-shadow: ${({ $buttonHoverColour }) =>
      $buttonHoverColour
        ? `0 0 0 0.25rem ${lighten(0.05, $buttonHoverColour)}`
        : "none"};
  }
`;

const StyledWrapper = styled.div`
  flex: ${({ $isInline, $isEvenBoxSizes }) =>
    $isInline ? `1 0 ${$isEvenBoxSizes ? "0%" : "25%"}` : "1 1 100%"};
  @media (max-width: ${breakpoints.DESKTOP}) {
    flex: ${({ $isInline }) => ($isInline ? "1 0 40%" : "1 1 100%")};
  }
`;

const StyledBottomWrapper = styled.div`
  flex: ${({ $isInline }) => ($isInline ? "1 0 25%" : "1 1 100%")};
  gap: 1rem;
  @media (max-width: ${breakpoints.DESKTOP}) {
    flex: ${({ $isInline }) => ($isInline ? "1 0 40%" : "1 1 100%")};
  }
  @media (max-width: ${breakpoints.TABLET}) {
    flex-direction: ${({ $hasButtonText, $isInline }) =>
      $hasButtonText && !$isInline ? "column" : "row"};
  }
`;

const StyledText = styled.span`
  font-size: 12px;
  @media (min-width: ${breakpoints.DESKTOP}) {
    font-size: 1rem;
  }
`;

const StyledStickyWrapper = styled.div`
  ${({ $isFixed, $stickyTop, $stickyPadding, theme }) =>
    $isFixed
      ? `
    position: fixed;
    top: ${$stickyTop || "12rem"};
    left: 0;
    z-index: 1000;
    width: 100%;
    padding: ${$stickyPadding || "1rem 0"};
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  `
      : ""}
  
  /* Disable fixed positioning on mobile devices */
  @media (max-width: ${breakpoints.TABLET}) {
    position: static !important;
  }
`;

const StyledForm = styled.form`
  width: 100%;
`;

const StyledFindYourRole = styled.div`
  flex-wrap: ${({ $isInline }) => ($isInline ? "nowrap" : "wrap")};
`;

const locationSelectStyles = {
  container: (provided) => ({ ...provided, width: "100%" }),
  control: (provided) => ({
    ...provided,
    border: "border 1px #ccc",
    borderRadius: 0,
    boxShadow: "none",
    height: "3rem",
  }),
  menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
  placeholder: (provided) => ({
    ...provided,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
};

function FindYourRole(props) {
  const {
    fieldsConfiguration,
    isInline,
    isEvenBoxSizes,
    onSubmit,
    text = "Find your role",
    buttonText,
    buttonColor,
    buttonHoverColour,
    buttonTextColour,
    withoutBottomMargin,
    initialValue,
    isSticky,
    stickyTop,
    stickyPadding,
  } = props;

  // Refs and state - hooks must be called unconditionally
  // They're only used when sticky is enabled (checked in useEffect)
  const wrapperRef = useRef(null);
  const formRef = useRef(null);
  const [isFixed, setIsFixed] = useState(false);

  useEffect(() => {
    if (!isSticky || typeof window === "undefined") return;

    const TABLET_BREAKPOINT = parseInt(breakpoints.TABLET, 10);
    const isMobile = () => window.innerWidth < TABLET_BREAKPOINT;

    const handleScroll = () => {
      if (!formRef.current || !wrapperRef.current) return;

      // Disable sticky behavior on mobile devices
      if (isMobile()) {
        if (isFixed) {
          setIsFixed(false);
        }
        return;
      }

      // Find the parent section (hero section) - look for common parent containers
      let parentSection = wrapperRef.current.closest('[class*="FindSection"]') || 
                         wrapperRef.current.closest('section') ||
                         wrapperRef.current.parentElement?.parentElement?.parentElement;
      
      if (!parentSection) {
        // Fallback: use the wrapper's offset parent or find a container with background color
        parentSection = wrapperRef.current.closest('div[style*="background"]') || 
                       wrapperRef.current.parentElement;
      }

      if (!parentSection) return;

      const parentRect = parentSection.getBoundingClientRect();
      const stickyTopValue = parseFloat((stickyTop || "12rem").replace("rem", "")) * 16 || 192;
      
      // Calculate when hero is nearly off the page (e.g., when bottom is at stickyTop position)
      // This means the hero section's bottom edge is at or above the sticky position
      const heroBottom = parentRect.bottom;
      const shouldBeFixed = heroBottom <= stickyTopValue + 50; // 50px threshold for "nearly off"

      if (shouldBeFixed !== isFixed) {
        setIsFixed(shouldBeFixed);
      }
    };

    const handleResize = () => {
      // Reset sticky state when crossing breakpoint
      if (isMobile() && isFixed) {
        setIsFixed(false);
      }
      handleScroll();
    };

    // Don't set up scroll listeners on mobile
    if (isMobile()) {
      if (isFixed) {
        setIsFixed(false);
      }
      return;
    }

    // Use requestAnimationFrame for smoother updates
    let rafId = null;
    let ticking = false;

    const onScroll = () => {
      if (!ticking) {
        rafId = requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    // Initial check
    handleScroll();

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", handleResize, { passive: true });

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, [isSticky, stickyTop, isFixed]);

  const { sendTrackingEvent } = useAnalytics();
  const { filtersStore } = useStores();
  const { pushToDataLayer } = useDataLayer();
  const { setFilter, nonEmptyFilters, filters } = filtersStore;

  const fieldsWithoutLast = fieldsConfiguration?.slice(0, -1);
  const lastField = fieldsConfiguration[fieldsConfiguration.length - 1];

  const marginClassNames = classNames({
    "mb-3": !isInline,
    "me-3 mb-0": isInline,
  });

  const handleLocationSelect = (selectedLocation) => {
    setFilter("locationLabel", null);
    if (selectedLocation?.coordsMode) {
      const locationObject = {
        coords: { lat: selectedLocation?.lat, lng: selectedLocation?.long },
        label: selectedLocation?.label,
        value: selectedLocation?.value,
      };
      setFilter("location", selectedLocation ? locationObject : null);
    } else {
      setFilter(
        "locationLabel",
        selectedLocation ? selectedLocation.city : null
      );
      if (!selectedLocation?.location) {
        setFilter("location", null);
      }
    }
  };

  const styledFindYourRoleClassNames = classNames("align-items-center d-flex", {
    "mb-4": !withoutBottomMargin,
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    const newFilters = [];
    if (nonEmptyFilters.category) {
      newFilters.push(nonEmptyFilters.category);
    }

    if (nonEmptyFilters.keywords) {
      newFilters.push(nonEmptyFilters.keywords);
    }

    if (
      nonEmptyFilters.location &&
      nonEmptyFilters.location.value &&
      nonEmptyFilters.location.value.city
    ) {
      newFilters.push(nonEmptyFilters.location.value.city);
    }

    const searchTerm = newFilters.join("-");

    sendTrackingEvent("USER_ENAGEMENT_SEARCH", searchTerm);

    pushToDataLayer("userSearch", { filters: nonEmptyFilters });

    if (typeof onSubmit === "function") {
      onSubmit();
    }
  };

  const fieldToComponentMap = {
    category: { component: RoleSelect, props: {} },
    keywords: {
      component: KeywordField,
      props: { placeholder: "Job title or keywords" },
    },
    location: {
      component: LocationSelect,
      props: {
        onSelect: handleLocationSelect,
        initialValue: filters?.location?.label || initialValue?.location?.label,
        styles: locationSelectStyles,
      },
    },
  };

  const LastField = fieldToComponentMap[lastField?.field]?.component;

  const formContent = (
    <StyledForm
      ref={isSticky ? formRef : undefined}
      className="find-your-role"
      onSubmit={onSubmit}
    >
          <StyledFindYourRole
            className={styledFindYourRoleClassNames}
            $isInline={isInline}
          >
        {text && (
          <Markdown
            className={`text-white me-5 text-nowrap ${marginClassNames}`}
          >
            {text}
          </Markdown>
        )}

        {fieldsConfiguration?.length >= 2 &&
          fieldsWithoutLast.map((filter) => {
            const Component = fieldToComponentMap[filter.field].component;

            return (
              <StyledWrapper
                key={`${filter.field}`}
                className={`${marginClassNames} ${filter.field}`}
                $isEvenBoxSizes={isEvenBoxSizes}
                $isInline={isInline}
              >
                <Component
                  {...fieldToComponentMap[filter.field].props}
                  placeholder={filter.placeholder}
                />
              </StyledWrapper>
            );
          })}

        <StyledBottomWrapper
          className="d-flex bottom-wrapper"
          $hasButtonText={buttonText}
          $isInline={isInline}
        >
          <div className="bg-white d-flex align-items-center flex-grow-1 last-field">
            <LastField
              {...fieldToComponentMap[lastField?.field]?.props}
              placeholder={lastField?.placeholder}
            />
          </div>
          <StyledSearchButton
            $buttonColor={buttonColor}
            $buttonText={buttonText}
            $buttonHoverColour={buttonHoverColour}
            $buttonTextColour={buttonTextColour}
            aria-label="Search vacancies"
            className="d-flex justify-content-center align-items-center flex-shrink-0 border-0 search-button"
            height="3rem"
            name={buttonText ?? "search"}
            onClick={handleSubmit}
            type="submit"
          >
            {buttonText ? (
              <StyledText className="text-uppercase text-bold text-nowrap">
                {buttonText}
              </StyledText>
            ) : (
              <Icon icon="search" size={22} />
            )}
          </StyledSearchButton>
        </StyledBottomWrapper>
      </StyledFindYourRole>
    </StyledForm>
  );

  // Conditionally wrap with sticky wrapper only if sticky is enabled
  if (isSticky) {
    return (
      <StyledStickyWrapper
        ref={wrapperRef}
        $isFixed={isFixed}
        $stickyTop={stickyTop || "12rem"}
        $stickyPadding={stickyPadding || "1rem 0"}
      >
        {formContent}
      </StyledStickyWrapper>
    );
  }

  return formContent;
}

export default observer(FindYourRole);

FindYourRole.propTypes = {
  buttonColor: PropTypes.string,
  buttonHoverColour: PropTypes.string,
  buttonTextColour: PropTypes.string,
  buttonText: PropTypes.string,
  fieldsConfiguration: PropTypes.arrayOf(
    PropTypes.shape({ field: PropTypes.string, placeholder: PropTypes.string })
  ).isRequired,
  isEvenBoxSizes: PropTypes.bool,
  isInline: PropTypes.bool.isRequired,
  isSticky: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  stickyPadding: PropTypes.string,
  stickyTop: PropTypes.string,
  text: PropTypes.string,
  withoutBottomMargin: PropTypes.bool,
};

FindYourRole.defaultProps = {
  text: "Find your role",
  buttonText: null,
  buttonColor: null,
  buttonHoverColour: null,
  buttonTextColour: null,
  withoutBottomMargin: false,
  isEvenBoxSizes: false,
  isSticky: false,
  stickyTop: "12rem",
  stickyPadding: "1rem 0",
};
